import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteData, usePageContent } from '@/context/SiteDataContext';
import PageHero from '@/components/PageHero';
import { X, Mail, Phone, Facebook, Instagram } from 'lucide-react';

const DEFAULT_SECTION_ORDER = [
  'Episcopal Leadership',
  'Executive Administration',
  'District Leadership',
  'Pastors & Elders Council',
  'Ordination & Ministerial Development',
  'Adjutancy',
  'Jurisdictional Department Presidents',
];

export default function Leadership() {
  const { leaders } = useSiteData();
  const c = usePageContent('leadership');
  const [inquire, setInquire] = useState(null);
  const list = leaders || [];

  useEffect(() => { document.body.style.overflow = inquire ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [inquire]);

  const groups = new Map();
  list.forEach(l => { const key = l.section || 'Leadership'; if (!groups.has(key)) groups.set(key, []); groups.get(key).push(l); });
  const orderedKeys = [
    ...DEFAULT_SECTION_ORDER.filter(k => groups.has(k)),
    ...Array.from(groups.keys()).filter(k => !DEFAULT_SECTION_ORDER.includes(k)),
  ];
  const sectionTitleFor = (name) => {
    const idx = DEFAULT_SECTION_ORDER.indexOf(name);
    if (idx < 0) return { title: name, subtitle: '' };
    return { title: c[`section${idx+1}_title`] || name, subtitle: c[`section${idx+1}_subtitle`] || '' };
  };

  return (
    <div>
      <PageHero slug="leadership" overline="Servant Leadership" heading="Our Leadership" text="Meet the men and women called to shepherd, teach, and guide the Wisconsin First Jurisdiction." />

      <div className="bg-white py-16 lg:py-20">
        {orderedKeys.map(sectionName => {
          const t = sectionTitleFor(sectionName);
          return (
            <section key={sectionName} className="max-w-7xl mx-auto px-6 mb-16">
              <div className="mb-10">
                {t.subtitle && <div className="overline mb-3">{t.subtitle}</div>}
                <h2 className="font-serif text-3xl lg:text-4xl text-royal font-bold gold-underline">{t.title}</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {groups.get(sectionName).map(l => <LeaderCard key={l.id} l={l} onInquire={() => setInquire(l)} />)}
              </div>
            </section>
          );
        })}
      </div>

      {inquire && <InquireModal l={inquire} onClose={() => setInquire(null)} />}
    </div>
  );
}

function LeaderCard({ l, onInquire }) {
  return (
    <div data-testid={`leader-card-${l.id}`} className="card-tactile overflow-hidden flex flex-col">
      <div className="aspect-square overflow-hidden relative" style={{ backgroundColor: '#F1E9D6' }}>
        {l.photo ? (
          <img src={l.photo} alt={l.name} className="w-full h-full object-cover"
            style={{ objectPosition: l.photoPosition || 'center top', transform: l.photoScale && l.photoScale !== 1 ? `scale(${l.photoScale})` : undefined, transformOrigin: 'center top' }} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-royal-dark to-royal">
            <div className="w-24 h-24 rounded-full border-2 border-gold flex items-center justify-center mb-4">
              <span className="font-serif text-gold text-3xl font-bold">{l.name?.split(' ').filter(w => w.length > 2 && !['Dr.', 'Sr.', 'Jr.'].includes(w)).slice(-2).map(w => w[0]).join('')}</span>
            </div>
            <div className="text-gold text-[10px] tracking-[0.2em] uppercase">Portrait Coming Soon</div>
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="text-xs text-gold tracking-widest uppercase font-semibold">{l.role}</div>
        <h3 className="font-serif text-2xl text-royal font-bold mt-2">{l.name}</h3>
        <p className="text-charcoal-muted mt-3 text-sm leading-relaxed">{l.bio}</p>
        {Array.isArray(l.responsibilities) && l.responsibilities.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-royal font-semibold mb-2">Ministry Responsibilities</div>
            <ul className="text-xs text-charcoal-muted space-y-1">{l.responsibilities.map((r, i) => <li key={`${l.id}-r-${i}`}>· {r}</li>)}</ul>
          </div>
        )}
        <div className="mt-auto pt-5">
          <button onClick={onInquire} data-testid={`inquire-btn-${l.id}`} className="w-full btn-royal justify-center text-sm py-2.5">Inquire / Contact</button>
        </div>
      </div>
    </div>
  );
}

function InquireModal({ l, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-charcoal-muted hover:text-royal"><X className="w-5 h-5" /></button>
        <div className="text-center">
          <div className="w-24 h-24 rounded-full overflow-hidden mx-auto border-4 border-gold" style={{ backgroundColor: '#F1E9D6' }}>
            {l.photo && <img src={l.photo} alt="" className="w-full h-full object-cover" style={{ objectPosition: l.photoPosition || 'center top', transform: l.photoScale ? `scale(${l.photoScale})` : undefined, transformOrigin: 'center top' }} />}
          </div>
          <div className="overline mt-4">{l.role}</div>
          <h3 className="font-serif text-2xl text-royal font-bold mt-1">{l.name}</h3>
        </div>
        <div className="mt-6 space-y-3">
          {l.contact_email && <ContactRow icon={Mail} label="Email" value={l.contact_email} href={`mailto:${l.contact_email}`} testid={`inquire-email-${l.id}`} />}
          {l.contact_phone && <ContactRow icon={Phone} label="Phone" value={l.contact_phone} href={`tel:${l.contact_phone}`} />}
          {l.facebook_url && <ContactRow icon={Facebook} label="Facebook" value="Visit profile" href={l.facebook_url} external />}
          {l.instagram_url && <ContactRow icon={Instagram} label="Instagram" value="Visit profile" href={l.instagram_url} external />}
          {!l.contact_email && !l.contact_phone && !l.facebook_url && !l.instagram_url && (
            <div className="text-center text-charcoal-muted text-sm py-4">Contact information not yet available.<br /><Link to="/contact" className="text-royal underline">Reach out to the jurisdictional office</Link>.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testid }) {
  const props = external ? { target: '_blank', rel: 'noreferrer' } : {};
  return (
    <a href={href} {...props} data-testid={testid} className="flex items-center gap-3 p-3 rounded-lg border hover:border-royal hover:bg-royal/5 transition">
      <div className="w-10 h-10 rounded-full bg-royal/10 flex items-center justify-center"><Icon className="w-5 h-5 text-royal" /></div>
      <div className="flex-1"><div className="text-xs text-charcoal-muted">{label}</div><div className="text-sm text-royal font-semibold">{value}</div></div>
    </a>
  );
}
